import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getVendorMappedAction,
  deleteVendorMappingAction,
} from "../../../../redux/actions/settings-action";
import DynamicTransactionTable from "../../../tables/dynamic-transaction-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import successToast from "../../../helper-components/success-toast";
import errorToast from "../../../helper-components/error-toast";

// GRN-navbar styling: fields sit inside the billing-head bar using the shared
// search-input theme variables (matches sales-purchase-navbar).
const FIELD_WRAP = "flex flex-col w-[220px] min-w-0";
const LABEL_CLS =
  "min-h-[1.2rem] mb-1 text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]";
const INPUT_CLS =
  "min-h-[40px] px-2 border border-[var(--search-input-border)] focus:outline-none focus:border-[var(--search-input-focus)] bg-[var(--search-input-bg)] text-[var(--search-input-color)] text-[length:var(--search-input-font-size)] font-[var(--search-input-font-weight)] border-radius input-field-no-spin-buttons placeholder:text-[var(--search-place-color)]";

const ViewVendorCatalouge = () => {
  const dispatch = useDispatch();
  const { id } = useParams(); // vendor id (cmr_id) from the view URL

  const [loading, setLoading] = useState(false);
  const [tableBodyData, setTableBodyData] = useState([]);
  const [reload, setReload] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dataCount, setDataCount] = useState(0);

  // row pending deletion (drives the confirmation popup)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // free-text search filters
  const [filters, setFilters] = useState({
    vendor_item_code: "",
    vendor_item_name: "",
    item_code: "",
    item_name: "",
  });
  // debounced copy actually sent to the server
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // debounce typing, and reset to the first page whenever filters change
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [filters]);

  const fetchMappedData = useCallback(() => {
    setLoading(true);
    dispatch(
      getVendorMappedAction(
        {
          vendor_id: id,
          ...debouncedFilters,
          page: currentPage,
          limit: pageSize,
        },
        (res) => {
          // tolerate both shapes: { data: [...], total } and a plain array
          const payload = res?.data;
          const list = Array.isArray(payload) ? payload : payload?.data || [];
          const total = Array.isArray(payload)
            ? list.length
            : payload?.total ?? list.length;

          const rows = list.map((r, idx) => ({
            id: `${currentPage}_${idx}`,
            item_mapping_id: r.item_mapping_id,
            vendor_code: r.vendor_code,
            vendor_name: r.vendor_name,
            vendor_item_code: r.vendor_item_code,
            vendor_item_name: r.vendor_item_name,
            vendor_uom: r.vendor_uom,
            system_item_code: r.system_item_code,
            system_item_name: r.system_item_name,
            system_item_uom: r.system_item_uom,
          }));
          setTableBodyData(rows);
          setDataCount(total);
          setLoading(false);
        },
        () => {
          setTableBodyData([]);
          setDataCount(0);
          setLoading(false);
        }
      )
    );
  }, [dispatch, id, debouncedFilters, currentPage, pageSize]);

  useEffect(() => {
    fetchMappedData();
  }, [fetchMappedData, reload]);

  const paginationProps = {
    pageSize,
    setPageSize,
    dataCount,
    setCurrentPage,
    currentPage,
  };

  const onFilterChange = (key, val) =>
    setFilters((prev) => ({ ...prev, [key]: val }));

  const confirmDelete = () => {
    if (!deleteTarget?.item_mapping_id) {
      errorToast("Unable to delete this mapping (missing id).");
      return;
    }
    setDeleting(true);
    dispatch(
      deleteVendorMappingAction(
        { item_mapping_id: deleteTarget.item_mapping_id, vendor_id: id },
        () => {
          setDeleting(false);
          setDeleteTarget(null);
          successToast("Mapping deleted successfully");
          setReload((r) => !r);
        },
        (err) => {
          setDeleting(false);
          errorToast(err?.message || "Failed to delete mapping");
        }
      )
    );
  };

  const heading = useMemo(
    () => [
      { label: "Vendor Item Code", value: "vendor_item_code", minWidth: 140, column: true, display: false, enabled: true },
      { label: "Vendor Item Name", value: "vendor_item_name", minWidth: 180, column: true, display: false, enabled: true },
      { label: "Vendor UOM", value: "vendor_uom", minWidth: 120, column: true, display: false, enabled: true },
      { label: "System Item Code", value: "system_item_code", minWidth: 140, column: true, display: false, enabled: true },
      { label: "System Item Name", value: "system_item_name", minWidth: 220, column: true, display: false, enabled: true },
      { label: "System Item UOM", value: "system_item_uom", minWidth: 160, column: true, display: false, enabled: true },
      { label: "", value: "row_action", minWidth: 70, column: true, display: false, enabled: true },
    ],
    []
  );

  const tableRows = useMemo(
    () =>
      tableBodyData.map((row) => ({
        ...row,
        row_action: (
          <div
            className="flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="text-[var(--create-table-delete-color)] hover:opacity-80"
              title="Delete mapping"
              onClick={() => setDeleteTarget(row)}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ),
      })),
    [tableBodyData]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="px-5 py-3">
        <div className="bg-[var(--add-billing-head-bg)] border-radius flex flex-wrap items-end gap-3 px-3 py-3 w-full">
        <div className={FIELD_WRAP}>
          <label className={LABEL_CLS}>Vendor Item Code</label>
          <input
            className={INPUT_CLS}
            value={filters.vendor_item_code}
            onChange={(e) => onFilterChange("vendor_item_code", e.target.value)}
            placeholder="Search Vendor Item Code"
          />
        </div>

        <div className={FIELD_WRAP}>
          <label className={LABEL_CLS}>Vendor Item Name</label>
          <input
            className={INPUT_CLS}
            value={filters.vendor_item_name}
            onChange={(e) => onFilterChange("vendor_item_name", e.target.value)}
            placeholder="Search Vendor Item Name"
          />
        </div>

        <div className={FIELD_WRAP}>
          <label className={LABEL_CLS}>System Item Code</label>
          <input
            className={INPUT_CLS}
            value={filters.item_code}
            onChange={(e) => onFilterChange("item_code", e.target.value)}
            placeholder="Search System Item Code"
          />
        </div>

        <div className={FIELD_WRAP}>
          <label className={LABEL_CLS}>System Item Name</label>
          <input
            className={INPUT_CLS}
            value={filters.item_name}
            onChange={(e) => onFilterChange("item_name", e.target.value)}
            placeholder="Search System Item Name"
          />
        </div>
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <DynamicTransactionTable
          transaction="Business Catalogue"
          transaction_type="bussiness_catalogue"
          label="Mapping"
          module={"bussiness-catalouge-setup"}
          path="bussiness-catalouge"
          filterSec={false}
          tableHeading={heading}
          tableBodyData={tableRows}
          paginationProps={paginationProps}
          filter={false}
          list={false}
          menu={false}
          is_checkbox_req={false}
          setLoading={setLoading}
          reload={reload}
          setReload={setReload}
          transactionKey="bc"
          date={false}
          showClear={false}
          addNew={false}
          showFilterArrows={false}
          showPagination={true}
        />
      </div>

      {/* Delete confirmation popup */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[9999] grid place-items-center">
          <div
            className="absolute inset-0 bg-black opacity-30"
            onClick={() => !deleting && setDeleteTarget(null)}
          />
          <div className="relative w-[90%] max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-800">
              Delete Mapping
            </h3>
            <p className="mt-2 text-sm text-gray-600">
              Are you sure you want to delete the mapping for{" "}
              <span className="font-medium">
                {deleteTarget.vendor_item_name || deleteTarget.vendor_item_code}
              </span>
              ? This action cannot be undone.
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                disabled={deleting}
                className="px-4 py-2 rounded-lg border border-[var(--remove-color)] bg-[var(--remove-color)] text-white hover:opacity-90 disabled:opacity-60"
                onClick={() => setDeleteTarget(null)}
              >
                No
              </button>
              <button
                type="button"
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-[var(--primary)] text-white hover:opacity-90 disabled:opacity-60"
                onClick={confirmDelete}
              >
                {deleting ? "Deleting..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewVendorCatalouge;
